<template>
  <section id="decks-index" :class="{'title-hidden': !showTitle}" class="deck-index section m-b-50">
    <div class="container deck-container has-text-centered">
      <div v-if="showTitle && showOnboarding" class="container deck-container live-demo m-b-50">
        <div class="notification has-text-centered is-primary" style="border-radius: 10px; padding-bottom: 2.5em;">
          <h2 class="title is-4 m-t-30"><span class="emoji">✨</span> Pick a popular deck below and duplicate it into your account</h2>
          <!-- <p class="subtitle is-5" style="max-width: 700px;margin: -.35em auto 1em;">Browse the popular slide deck templates below to find one you like, then duplicate it into your account!</p> -->
          <n-link to="#tag-list" class="button is-medium is-primary is-inverted">Browse Popular Decks 👇</n-link>
        </div>
      </div>

      <div v-if="publicDecks.length < 1" class="deck-box m-t-40">
        <div v-for="num in 6" :key="num" class="deck-card">
          <div class="deck-thumb">
            <b-skeleton height="230px" />
          </div>
        </div>
      </div>

      <!-- POPULAR PUBLIC DECKS -->
      <span id="main-title" style="height:1px" />
      <slot v-if="showTitle">
        <h2 class="title is-2 m-t-30 is-capitalized" data-cy="page-title">
          {{ activeTag ? '' + activeTag + ' Templates': 'Interactive Slide Templates' }}
        </h2>
        <p id="shown-title" class="subtitle is-4">Find the perfect template for more engaging events and meetings</p>
      </slot>

      <div id="tag-list" v-if="showTags" class="deck-list-tags" data-cy="popular-decks-tag-list">
        <n-link :to="'/templates'" :class="{'is-active': activeTag === false}" class="tag is-secondary is-medium m-r-5">all</n-link>
        <n-link
          :to="getTagUrl(tagName)"
          v-for="tagName in allTags"
          :key="tagName"
          :class="{'is-active': activeTag === tagName}"
          class="tag is-secondary is-medium m-r-5"
          data-cy="tag-link"
        >
          {{ tagName }}
        </n-link>
      </div>

      <template v-if="activeTag === false">
        <div v-for="(decks, tagName) in decksByTagName" :key="tagName" :id="'tag-' + toKebabCase(tagName)" class="category-box" data-cy="tag-group">
          <h2 class="title is-4 title-case" data-cy="tag-group-title">{{ tagName }}</h2>
          <DeckList :decks="decks ? decks.slice(0,6) : []" />
          <div class="buttons has-text-centered">
            <n-link :to="getTagUrl(tagName)" class="button is-steel" data-cy="tag-group-more-button">
              More from {{ tagName }}
            </n-link>
          </div>

          <div v-if="tagName === 'icebreaker'" style="margin: 0 3em 2.75em 2em;">
            <HelpfulUpgrade />
          </div>
        </div>
      </template>

      <template v-else>
        <div :id="'tag-' + toKebabCase(activeTag)" class="category-box">
          <h2 class="title is-4 title-case">{{ activeTag }}</h2>
          <DeckList :decks="decksByTagName[activeTag]" />
          <div class="buttons">
            <n-link :to="'/' + returnPath" class="button is-steel">&larr; Back to all categories</n-link>
          </div>
        </div>
        <HelpfulUpgrade />
      </template>
    </div>
  </section>
</template>
<script>
import HelpfulUpgrade from '~/components/HelpfulUpgrade'
import DeckList from '~/components/DeckList'
import { tagList } from '~/common/deck-tags.js'
import { getPublicDecks } from '~/queries/getPublicDecks'
import { popularDeckIds } from '~/common/decks'

const deckTagMap = [
  { tag: 'meeting / management', name: 'Team Meetings' },
  { tag: 'poll / survey', name: 'Poll / Survey' },
  { tag: 'team building', name: 'Team Building' },
  { tag: 'education', name: 'Classroom' },
  { tag: 'icebreaker', name: 'Icebreaker' },
  { tag: 'trivia', name: 'Trivia & Quiz' },
  { tag: 'seasonal & celebration', name: 'Seasonal & Celebration' },
  { tag: 'employee wellness', name: 'Wellness' },
  { tag: 'conference / event', name: 'Conference / Event' },
  { tag: 'company culture', name: 'Company Culture' },
  { tag: 'brainstorming', name: 'Brainstorming' },
  { tag: 'happy hour', name: 'Social Gathering' },
  { tag: 'social event', name: 'Social Gathering' }
]

export default {
  auth: false,
  components: {
    DeckList,
    HelpfulUpgrade
  },
  props: {
    tag: {
      type: String,
      default: undefined
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showTags: {
      type: Boolean,
      default: true
    },
    originPath: {
      type: String,
      default: 'popular'
    }
  },
  data () {
    return {
      publicDecks: []
    }
  },
  apollo: {
    publicDecks: {
      query: getPublicDecks,
      variables: {
        ids: popularDeckIds
      },
      // eslint-disable-next-line object-shorthand
      update: function (data) {
        this.setPopularDecks(data.slideDecks)
        return data.slideDecks
      }
    }
  },
  computed: {
    returnPath () {
      if (this.$route.query.from) {
        return this.$route.query.from
      } else {
        return this.originPath
      }
    },
    activeTag () {
      return this.tag ? this.tagFromKebab(this.tag) : false
    },
    loading () {
      return !this.slideDecks && this.slideDecks[0]
    },
    decksByTagName () {
      const decks = {}
      deckTagMap.forEach((cat) => {
        // cat.name in lower case
        const name = cat.name.toLowerCase()
        if (decks[name]) {
          decks[name] = decks[name].concat(this.decksByTag[cat.tag])
        } else {
          decks[name] = this.decksByTag[cat.tag]
        }
      })
      return decks
    },
    decksByTag () {
      const decksByTag = {}
      this.publicDecks.forEach((deck) => {
        const tags = deck.metaData.tags || []
        tags.forEach((tag) => {
          const tagIndex = tags.indexOf(tag)
          const tagDeck = {
            tagIndex,
            deck
          }

          if (decksByTag[tag]) {
            decksByTag[tag].push(tagDeck)
          } else {
            decksByTag[tag] = [tagDeck]
          }
        })
      })

      for (const tag in decksByTag) {
        if (decksByTag[tag].length < 3) {
          delete decksByTag[tag]
          continue
        }
        decksByTag[tag].sort((a, b) => {
          return a.tagIndex - b.tagIndex
        })

        decksByTag[tag] = decksByTag[tag].map(tagObj => tagObj.deck)
      }

      return decksByTag
    },
    allTags () {
      return Object.keys(this.decksByTagName)
    },
    showOnboarding () {
      const user = this.$store.state.user
      return this.$auth.loggedIn && user.slideDeckCount < 1
    }
  },
  mounted () {
    this.publicDecks = this.$store.state.popularDecks
  },
  methods: {
    getTagUrl (tagName) {
      const origin = this.originPath === 'popular' ? '' : '?from=' + this.originPath
      return '/templates/' + this.toKebabCase(tagName) + origin
    },
    setPopularDecks (decks) {
      this.$store.commit('setPopularDecks', decks)
    },
    toKebabCase (string) {
      if (!string?.length) { return '' }
      return string.match(/([a-zA-Z]+)/g).join('-').toLowerCase()
    },
    tagFromKebab (kebab) {
      return tagList.find((tag) => {
        return kebab === this.toKebabCase(tag)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.section.title-hidden {
  padding: 0;

  .deck-container {
    padding: 0;
  }
}
.button.is-steel {
  background-color: #e2ebf1;
  color: #8498b1;
  border: none;
  margin: auto;
  transition: transform .167s ease;

  &:hover {
    transform: scale(1.05);
  }
}
.category-box .title.title-case {
  text-transform: capitalize;
  margin-left: 31px;
  font-size: 1.5em;
  margin-bottom: -15px;
}

.category-box {
  padding-top: 30px;
  text-align: left;

  .buttons {
    padding-right: 22px;
    margin-top: 1.8em;
  }
}

@media screen and (max-width: 1216px) {
  .deck-index {

    .deck-box {
      .deck-shadow:last-of-type {
        display: none;
      }
    }

  }
}

@media screen and (max-width: 809px) {
  .deck-index {

    .deck-box {
      .deck-card {
        margin-right: 10px;
        margin-left: 10px;
      }
      .deck-shadow {
        display: none;
      }
    }

  }
}

.deck-list-tags {
  margin: 40px auto 40px;
  max-width: 800px;

  .tag{
    cursor: pointer;
    color: var(--secondary);
    background-color: rgb(220, 230, 236);
    font-weight: bold;
    margin-right: 10px;
    margin-bottom: 10px;
    text-transform: capitalize;

    &.is-active{
      background-color: var(--secondary);
      color: white;
    }
  }
}

.deck-box {
  margin-top: 40px;
  justify-content: center;

  &.user-slides {
    margin-top: 30px;
    margin-bottom: 60px;
  }

  .deck-card {
    width: 330px;
    transform: translate3d(0,0,0);
    transition: transform .16s ease;

    &:hover {
      transform: scale(1.05);

    }

    &.deck-shadow {
        opacity: .25;
        transform: scale(1);
        background-color: rgba(0,0,0,.2);
        cursor: auto;
        // border: 1px dashed rgba(0,0,0,.3);

        .deck-thumb {
          background-color: rgba(255,255,255,.2);
          // background-color: rgba(0,0,0,.1);
        }
      }

    .pick-this {
      display: none;
      position: absolute;
      top: .2em;
      right: -1.5em;
      z-index: 3;
      // transition: transform .16s ease;
      // transition-delay: .16s;
      // transform:  scale(0) rotate(25deg);
    }
  }
}
.deck-index {
  background-color: #f3f5f9 ;
  margin-bottom: 0;
  padding-bottom: 150px;
}

.rainbow-bg {
  .button{
    background-color: rgba(255,255,255,.9);
    color:rgb(243, 35, 122);
    border: none;

    i {
      margin-top: 2px;
    }
  }
}

.meta-controls.buttons {
  width: 100%;
  height: 100%;
  top:0;
  right:0;
  display: flex;
  justify-content: center;
  align-items: center;

  i {
    position: relative;
    top: 2px;
    margin-right: 5px;
  }
}

.meta-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(230,230,230,.7);
  opacity: 0;
  transition: opacity .2s ease;
  padding: 6px 3px 3px;
  border-radius: 3px;
  color: white;
  font-size: .9em;
  display: flex;
  flex-direction: column;

  &.dark {
    background-color: rgba(0,0,0,.5);
  }

  i {
    padding: 6px 4px 9px;

    &:hover {
      color: rgb(243, 35, 122);
    }
  }
}
</style>
